"use client";
import React, { useEffect, useState } from "react";
import {
  Search,
  Plus,
  Edit,
  Trash2,
  X,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  FolderOpen,
  Image as ImageIcon,
} from "lucide-react";
import { useCategories } from "../context/CategoryContext";
import { IMG_URL } from "../config/config";

const cx = (...c) => c.filter(Boolean).join(" ");

const Pill = ({ children, tone = "gray", size = "md" }) => {
  const toneMap = {
    red: "bg-red-50 text-red-700 ring-red-100",
    blue: "bg-blue-50 text-blue-700 ring-blue-100",
    green: "bg-emerald-50 text-emerald-700 ring-emerald-100",
    gray: "bg-gray-50 text-gray-700 ring-gray-200",
    amber: "bg-amber-50 text-amber-700 ring-amber-100",
    violet: "bg-violet-50 text-violet-700 ring-violet-100",
    yellow: "bg-yellow-50 text-yellow-700 ring-yellow-100",
  };
  const sizeMap = {
    sm: "px-2.5 py-1 text-[11px]",
    md: "px-3 py-1.5 text-xs",
  };
  return (
    <span
      className={cx(
        "inline-flex items-center rounded-full font-extrabold ring-1 whitespace-nowrap",
        toneMap[tone] || toneMap.gray,
        sizeMap[size] || sizeMap.md
      )}
    >
      {children}
    </span>
  );
};

const SectionDivider = ({ label, right }) => (
  <div className="flex items-center gap-3 my-8">
    {label ? (
      <span className="inline-flex items-center rounded-full bg-white px-4 py-2 text-xs font-extrabold text-gray-800 ring-1 ring-gray-200 shadow-sm">
        {label}
      </span>
    ) : null}
    <div className="h-px flex-1 bg-gradient-to-r from-gray-200 via-gray-200 to-transparent" />
    {right ? <div className="flex items-center gap-2">{right}</div> : null}
  </div>
);

export default function CategoriesPage() {
  const {
    categories,
    loading,
    fetchCategories,
    addCategory,
    updateCategory,
    deleteCategory,
    toggleCategoryStatus,
  } = useCategories();

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editCategory, setEditCategory] = useState(null);

  const ITEMS_PER_PAGE = 5;

  useEffect(() => {
    fetchCategories();
  }, []);

  const filteredCategories = categories.filter((cat) =>
    cat.name.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(filteredCategories.length / ITEMS_PER_PAGE);
  const start = (page - 1) * ITEMS_PER_PAGE;
  const currentCategories = filteredCategories.slice(
    start,
    start + ITEMS_PER_PAGE
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-[#fff7f6] to-[#f9fafb]">
      <div className="p-6 sm:p-8">
        {/* HERO SECTION */}
        <div className="rounded-3xl bg-white shadow-sm ring-1 ring-gray-200 overflow-hidden mb-8">
          <div className="relative p-7 sm:p-10">
            <div className="pointer-events-none absolute -top-28 -left-28 h-72 w-72 rounded-full bg-red-200/25 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-28 -right-28 h-72 w-72 rounded-full bg-orange-200/25 blur-3xl" />

            <div className="relative flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
              <div className="max-w-3xl">
                <div className="flex flex-wrap items-center gap-2">
                  <Pill tone="red">
                    <FolderOpen className="h-4 w-4 mr-1" />
                    Category Management
                  </Pill>
                  <Pill tone="gray">Admin Portal</Pill>
                  <Pill tone="green">
                    <Sparkles className="h-4 w-4 mr-1" />
                    Organized Content
                  </Pill>
                </div>

                <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-gray-900">
                  Categories
                </h1>
                <p className="mt-3 text-gray-600 text-sm sm:text-base">
                  Manage and organize your content categories. Add, edit, or remove categories to keep your platform organized.
                </p>
              </div>

              <div className="flex items-center gap-3">
                <div className="rounded-2xl bg-gray-50 ring-1 ring-gray-200 px-4 py-3">
                  <div className="text-[11px] font-extrabold tracking-widest uppercase text-gray-600">Total</div>
                  <div className="text-sm font-semibold text-gray-900 mt-1">
                    {filteredCategories.length} categories
                  </div>
                </div>

                <button
                  onClick={() => setShowAddModal(true)}
                  className="inline-flex items-center gap-2 rounded-2xl bg-red-600 px-5 py-3 text-sm font-extrabold text-white hover:bg-red-700 transition shadow-sm"
                >
                  <Plus className="h-4 w-4" />
                  Add Category
                </button>
              </div>
            </div>
          </div>
        </div>

        <SectionDivider
          label="Browse Categories"
          right={
            <div className="flex items-center gap-2">
              {search && <Pill tone="amber" size="sm">Search: {search}</Pill>}
              <Pill tone="blue" size="sm">Page {page} of {totalPages}</Pill>
            </div>
          }
        />

        {/* SEARCH */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search categories..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="w-full pl-10 pr-4 py-3 rounded-2xl ring-1 ring-gray-200 focus:ring-2 focus:ring-red-500 outline-none"
            />
          </div>
        </div>

        {/* TABLE */}
        <div className="rounded-3xl bg-white ring-1 ring-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="px-6 py-4 text-left text-xs font-extrabold tracking-widest uppercase text-gray-600">
                    Image
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-extrabold tracking-widest uppercase text-gray-600">
                    Name
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-extrabold tracking-widest uppercase text-gray-600">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-extrabold tracking-widest uppercase text-gray-600">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading && (
                  <tr>
                    <td colSpan="4" className="px-6 py-8 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-red-600 border-t-transparent"></div>
                        <span className="text-sm text-gray-600">Loading categories...</span>
                      </div>
                    </td>
                  </tr>
                )}

                {!loading && currentCategories.map((cat, index) => (
                  <tr
                    key={cat.id}
                    className={cx(
                      "hover:bg-gray-50 transition-colors",
                      index !== currentCategories.length - 1 ? "border-b border-gray-100" : ""
                    )}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        {cat.image ? (
                          <img
                            src={`${IMG_URL}/categories/${cat.image}`}
                            alt={cat.name}
                            className="h-12 w-12 rounded-2xl object-cover ring-1 ring-gray-200"
                          />
                        ) : (
                          <div className="h-12 w-12 rounded-2xl bg-gray-100 ring-1 ring-gray-200 flex items-center justify-center">
                            <ImageIcon className="h-5 w-5 text-gray-400" />
                          </div>
                        )}
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="font-extrabold text-gray-900">{cat.name}</div>
                    </td>

                    <td className="px-6 py-4">
                      <button
                        onClick={() => toggleCategoryStatus(cat.id, cat.status)}
                        className="transition-all duration-200"
                      >
                        <Pill
                          tone={cat.status === "Active" ? "green" : "yellow"}
                          size="sm"
                        >
                          {cat.status}
                        </Pill>
                      </button>
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setEditCategory(cat);
                            setShowEditModal(true);
                          }}
                          className="p-2 rounded-2xl hover:bg-blue-50 text-blue-600 hover:text-blue-700 transition-colors"
                          aria-label="Edit category"
                        >
                          <Edit className="h-4 w-4" />
                        </button>

                        <button
                          onClick={() => deleteCategory(cat.id)}
                          className="p-2 rounded-2xl hover:bg-red-50 text-red-600 hover:text-red-700 transition-colors"
                          aria-label="Delete category"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}

                {!loading && currentCategories.length === 0 && (
                  <tr>
                    <td colSpan="4" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center">
                          <FolderOpen className="h-6 w-6 text-gray-400" />
                        </div>
                        <div>
                          <div className="font-extrabold text-gray-900">No categories found</div>
                          <p className="mt-1 text-sm text-gray-600">
                            {search ? "Try adjusting your search terms." : "Get started by adding your first category."}
                          </p>
                        </div>
                        {!search && (
                          <button
                            onClick={() => setShowAddModal(true)}
                            className="mt-2 inline-flex items-center gap-2 rounded-2xl bg-red-600 px-4 py-2 text-sm font-extrabold text-white hover:bg-red-700 transition"
                          >
                            <Plus className="h-4 w-4" />
                            Add Category
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* PAGINATION */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-3 mt-8">
            <button
              onClick={() => setPage(Math.max(page - 1, 1))}
              disabled={page === 1}
              className={cx(
                "p-3 rounded-2xl ring-1 ring-gray-200 transition-colors",
                page === 1
                  ? "opacity-50 cursor-not-allowed bg-gray-50"
                  : "bg-white hover:bg-gray-50"
              )}
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>

            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                <button
                  key={pageNum}
                  onClick={() => setPage(pageNum)}
                  className={cx(
                    "px-4 py-2 rounded-2xl text-sm font-extrabold transition-colors",
                    pageNum === page
                      ? "bg-red-600 text-white"
                      : "bg-white text-gray-700 hover:bg-gray-50 ring-1 ring-gray-200"
                  )}
                >
                  {pageNum}
                </button>
              ))}
            </div>

            <button
              onClick={() => setPage(Math.min(page + 1, totalPages))}
              disabled={page === totalPages}
              className={cx(
                "p-3 rounded-2xl ring-1 ring-gray-200 transition-colors",
                page === totalPages
                  ? "opacity-50 cursor-not-allowed bg-gray-50"
                  : "bg-white hover:bg-gray-50"
              )}
              aria-label="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* MODALS */}
        {showAddModal && (
          <CategoryModal
            title="Add Category"
            onClose={() => setShowAddModal(false)}
            onSave={(formData) => addCategory(formData)}
          />
        )}

        {showEditModal && editCategory && (
          <CategoryModal
            title="Edit Category"
            category={editCategory}
            onClose={() => {
              setShowEditModal(false);
              setEditCategory(null);
            }}
            onSave={(formData) => updateCategory(editCategory.id, formData)}
          />
        )}
      </div>
    </div>
  );
}

/* ================= MODAL ================= */

function CategoryModal({ title, onClose, onSave, category }) {
  const [name, setName] = useState(category?.name || "");
  const [status, setStatus] = useState(category?.status || "Active");
  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState(
    category?.image ? `${IMG_URL}/categories/${category.image}` : null
  );

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      alert("Please enter a category name");
      return;
    }

    const formData = new FormData();
    formData.append("name", name);
    formData.append("status", status);
    if (imageFile) formData.append("image", imageFile);

    await onSave(formData);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-md bg-white rounded-3xl ring-1 ring-gray-200 shadow-2xl overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-extrabold text-gray-900">{title}</h2>
            <button
              onClick={onClose}
              className="p-2 rounded-2xl hover:bg-gray-100 transition-colors"
              aria-label="Close modal"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-extrabold text-gray-900 mb-2">
              Category Name
            </label>
            <input
              type="text"
              placeholder="Enter category name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-3 rounded-2xl ring-1 ring-gray-200 focus:ring-2 focus:ring-red-500 outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-extrabold text-gray-900 mb-2">
              Category Image
            </label>
            <div className="space-y-3">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-2xl file:border-0 file:text-sm file:font-extrabold file:bg-gray-50 file:text-gray-700 hover:file:bg-gray-100"
              />

              {preview && (
                <div className="flex items-center gap-3">
                  <img
                    src={preview}
                    className="h-16 w-16 object-cover rounded-2xl ring-1 ring-gray-200"
                    alt="Preview"
                  />
                  <div className="text-sm text-gray-600">
                    Image preview
                  </div>
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-extrabold text-gray-900 mb-2">
              Status
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full px-4 py-3 rounded-2xl ring-1 ring-gray-200 focus:ring-2 focus:ring-red-500 outline-none"
            >
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>

          <button
            onClick={handleSave}
            className="w-full bg-red-600 text-white py-3 rounded-2xl font-extrabold hover:bg-red-700 transition-colors shadow-sm"
          >
            Save Category
          </button>
        </div>
      </div>
    </div>
  );
}
